import React from 'react';
import lazyGettext from 'js-utils/lazy-gettext';

import TicketFillChunkySvg from 'eventbrite_design_system/iconography/icons/TicketFillChunky';
import TicketFillOffChunkySvg from 'eventbrite_design_system/iconography/icons/TicketFillOffChunky';

const LABEL_CREATE_HELP_COST = 'WhatItCosts';
const LABEL_CREATE_HELP_HOW_TO = 'HowToCreate';
const LABEL_CREATE_PRODUCT_UPDATES = 'ProductUpdates';

export const DEFAULT_LANGUAGE = 'en_us';

export const ORG_LOGO_BK_COLOR = 'white';

export const ORG_ICON_COLOR = 'grey-400';
export const ORG_ICON_GREEN_COLOR = 'ui-green';
export const ORG_ICON_YELLOW_COLOR = 'tropical-yellow-500';
export const ON_SALE_EDS_COLOR = 'eds-text-color--ui-500';

export const PUBLISH_EVENT_INTENT = 'publish';
export const SCHEDULE_PUBLISH_EVENT_INTENT = 'schedule_publish';
export const CANCEL_SCHEDULE_EVENT_INTENT = 'cancel_schedule';
export const UNPUBLISH_EVENT_INTENT = 'unpublish';
export const CANCEL_EVENT_INTENT = 'cancel';
export const CHANGE_SALES_STATUS = 'change_sales_status';

export const LINK_FACEBOOK_EVENT = 'facebook.com/events/';
export const DIALOG_POWERED_BY_FACEBOOK_PRIMARY_TEXT = lazyGettext(
    'Visit my Facebook page',
);
export const DIALOG_POWERED_BY_FACEBOOK_MESSAGE = lazyGettext(
    'To change, edit, cancel or unlink your event, please visit your Facebook event page.',
);

export const CANCEL_MODAL_POSTPONE_CHOICE = 'postpone';
export const CANCEL_MODAL_CANCEL_CHOICE = 'cancel';
export const CANCEL_MODAL_DELETE_CHOICE = 'delete';
export const CANCEL_DELETE_RADIO_GROUP_DEFAULT = CANCEL_MODAL_POSTPONE_CHOICE;

export const CANCEL_DELETE_OPTIONS_DATA = [
    {
        value: CANCEL_MODAL_POSTPONE_CHOICE,
        headline: lazyGettext('Postpone Event'),
        description: lazyGettext(
            "Attendees can still see your event listing but can no longer purchase tickets. Remember to email your attendees to let them know you're postponing the event. To start selling tickets again, edit Ticket Sales end date in ticket settings.",
        ),
    },
    {
        value: CANCEL_MODAL_CANCEL_CHOICE,
        headline: lazyGettext('Cancel Event'),
        description: lazyGettext(
            "Attendees won't be able to access your event listing once you cancel. The event can't be republished.",
        ),
    },
    {
        value: CANCEL_MODAL_DELETE_CHOICE,
        headline: lazyGettext('Delete Event'),
        description: lazyGettext(
            "Your event listing and all of its information will be deleted permanently. This action can't be undone.",
        ),
    },
];

export const SCHEDULE_PUBLISH_DATA = {
    headline: lazyGettext('Schedule Publish'),
    description: lazyGettext("Time zone is the same as your event's"),
    datePickerLabel: lazyGettext('Publish On'),
    datePickerPlaceholder: lazyGettext('Select Publish Date'),
    timePickerPlaceholder: lazyGettext('Select Publish Time'),
    publicEventLinkToPrivacySettings: lazyGettext(
        'Your event will be <span class="eds-text-weight--heavy">Public</span>. Modify this in <a class="eds-link" href="%(privacySettingsLink)s">Privacy Settings</a>.',
    ),
    privateEventLinkToPrivacySettings: lazyGettext(
        'Your event will be <span class="eds-text-weight--heavy">Private</span>. Modify this in <a class="eds-link" href="%(privacySettingsLink)s">Privacy Settings</a>.',
    ),
    privateEventLinkToPrivacySettingsWithScheduledPublic: lazyGettext(
        'Your event will be <span class="eds-text-weight--heavy">Private</span>. Change this or schedule a date and time to make it Public in <a class="eds-link" href="%(privacySettingsLink)s">Privacy Settings</a>.',
    ),
    confirmSchedule: lazyGettext('Schedule'),
    cancelSchedule: lazyGettext('Cancel'),
};

export const COYOTE_EVENT_HELP = {
    path: '/support/articleredirect?anum=38056',
    content: lazyGettext('Product Updates'),
    gaLabel: LABEL_CREATE_PRODUCT_UPDATES,
};

export const COYOTE_HELP_URLS = [
    COYOTE_EVENT_HELP,
    {
        content: lazyGettext('How do I create an event?'),
        path: '/support/articleredirect?anum=41399',
        gaLabel: LABEL_CREATE_HELP_HOW_TO,
    },
    {
        content: lazyGettext('What does it cost to create an event?'),
        path: '/organizer/pricing/',
        gaLabel: LABEL_CREATE_HELP_COST,
    },
];

export const COYOTE_HELP_CENTER_URL_MAP = {
    en_US: '/support/lifestages?focus=NewEventCreationExperience&lg=en_US',
    en_GB: '/support/lifestages?focus=NewEventCreationExperience&lg=en_GB',
    en_IE: '/support/lifestages?focus=NewEventCreationExperience&lg=en_IE',
    en_CA: '/support/lifestages?focus=NewEventCreationExperience&lg=en_CA',
    en_AU: '/support/lifestages?focus=NewEventCreationExperience&lg=en_AU',
    en_HK: '/support/lifestages?focus=NewEventCreationExperience&lg=en_HK',
    en_SG: '/support/lifestages?focus=NewEventCreationExperience&lg=en_SG',
    en_NZ: '/support/lifestages?focus=NewEventCreationExperience&lg=en_NZ',
    nl_NL:
        '/support/lifestages?focus=NieuweEventbriteEvenementcreatie&lg=nl_NL',
    de_DE:
        '/support/lifestages?focus=NeuerBenutzeroberflacheZurEventerstellung&lg=de',
    it_IT:
        '/support/lifestages?focus=NuovaEsperienzaDiCreazioneDegliEventi&lg=it',
    fr_FR:
        '/support/lifestages?focus=NouvelleExperienceDeCreationDunEvenement&lg=fr',
    fr_CA:
        '/support/lifestages?focus=NouvelleExperienceDeCreationDEvenements&lg=fr_CA',
    es_ES:
        '/support/lifestages?focus=NuevaExperienciaDeCreacionDeEventos&lg=es',
    es_AR:
        '/support/lifestages?focus=NuevaExperienciaDeCreacionDeEventos&lg=es_AR',
    pt_PT:
        '/support/lifestages?focus=NovaExperienciaDeCriacaoDeEventos&lg=pt_PT',
    pt_BR:
        '/support/lifestages?focus=NovaExperienciaDeCriacaoDeEventos&lg=pt_BR',
    nl_BE:
        '/support/lifestages?focus=NieuweEventbriteEvenementcreatie&lg=nl_NL',
    de_AT:
        '/support/lifestages?focus=NeuerBenutzeroberflacheZurEventerstellung&lg=de',
};

export const EVENT_STATUS_BUTTON_UNPUBLISHED =
    'event-status-button-unpublished';
export const EVENT_STATUS_BUTTON_PUBLISH_SCHEDULED =
    'event-status-publish-button-scheduled';
export const EVENT_STATUS_BUTTON_PUBLISHED = 'event-status-button-published';
export const EVENT_STATUS_BUTTON_CANCELLED = 'event-status-button-cancelled';
export const EVENT_STATUS_BUTTON_ENDED = 'event-status-button-ended';

export const SCHEDULED_PUBLISH_URL_PARAM = 'schedulepublish';
export const UNSCHEDULE_PUBLISH_URL_PARAM = 'unschedulepublish';
export const CHANGE_SALES_STATUS_URL_PARAM = 'changesalesstatus';
export const CANCEL_EVENT_URL_PARAM = 'cancelevent';
export const ACTION_URL_PARAM = 'action';

const CANCELED_EVENT_LABEL = lazyGettext('Event Canceled');
const CANCELED_LABEL = lazyGettext('Canceled');
const CANCEL_PUBLISH_TIME_LABEL = lazyGettext('Cancel Publish Time');
const CHANGE_STATUS_LABEL = lazyGettext('Change Status');
const ENDED_EVENT_LABEL = lazyGettext('Event Ended');
const PREVIEW_LABEL = lazyGettext('Preview');
const PUBLIC_NOW_LABEL = lazyGettext('Publish Now');
const PUBLISHED_LABEL = lazyGettext('Published');
const PUBLISH_EVENT_LABEL = lazyGettext('Publish Event');
const PUBLISH_SCHEDULED_EVENT_LABEL = lazyGettext('Publish Scheduled');
const RESCHEDULE_PUBLISH_EVENT_LABEL = lazyGettext('Reschedule');
const SCHEDULE_PUBLISH_EVENT_LABEL = lazyGettext('Schedule Publish');
const SCHEDULED_EVENT_LABEL = lazyGettext('Scheduled');
const UNPUBLISH_EVENT_LABEL = lazyGettext('Unpublish Event');
const UNSCHEDULE_EVENT_LABEL = lazyGettext('Unschedule');
const VIEW_LABEL = lazyGettext('View');
const PUBLISH_ON_EVENT_LABEL = lazyGettext('Publish On:');
const ON_SALE_EVENT_LABEL = lazyGettext('On Sale');
const ON_SALE_SOON_EVENT_LABEL = lazyGettext('On Sale Soon');
const AT_THE_DOOR_EVENT_LABEL = lazyGettext('At The Door');

export const HEADER_LABELS = {
    CANCELED_EVENT_LABEL,
    CANCELED_LABEL,
    CANCEL_PUBLISH_TIME_LABEL,
    CHANGE_STATUS_LABEL,
    ENDED_EVENT_LABEL,
    PREVIEW_LABEL,
    PUBLIC_NOW_LABEL,
    PUBLISHED_LABEL,
    PUBLISH_EVENT_LABEL,
    PUBLISH_SCHEDULED_EVENT_LABEL,
    RESCHEDULE_PUBLISH_EVENT_LABEL,
    SCHEDULE_PUBLISH_EVENT_LABEL,
    SCHEDULED_EVENT_LABEL,
    UNPUBLISH_EVENT_LABEL,
    UNSCHEDULE_EVENT_LABEL,
    VIEW_LABEL,
    PUBLISH_ON_EVENT_LABEL,
    ON_SALE_EVENT_LABEL,
    ON_SALE_SOON_EVENT_LABEL,
    AT_THE_DOOR_EVENT_LABEL,
};

export const NARROW_ICON_TICKET_FILL_SALES_STATUS = <TicketFillChunkySvg />;
export const NARROW_ICON_TICKET_FILL_OFF_SALES_STATUS = (
    <TicketFillOffChunkySvg />
);

// Event Sales Status
export const EVENT_SALES_STATUS_MESSAGE_CODE_TICKETS_AT_THE_DOOR =
    'tickets_at_the_door';

export const EVENT_SALES_STATUS_NOT_YET_ON_SALE = 'not_yet_on_sale';
export const EVENT_SALES_STATUS_NOT_YET_ON_SALE_ACCESS_CODED =
    'not_yet_on_sale_access_coded';
export const EVENT_SALES_STATUS_SALES_ENDED = 'sales_ended';
export const EVENT_SALES_STATUS_ON_SALE = 'on_sale';
export const EVENT_SALES_STATUS_DEFAULT_VALUES = 'default';
export const EVENT_SALES_STATUS_ICON_AND_COLOR_MAP = {
    [EVENT_SALES_STATUS_ON_SALE]: {
        eventStatusLabel: lazyGettext('On Sale'),
        eventStatusIconColor: ORG_ICON_GREEN_COLOR,
        eventStatusNarrowIconType: NARROW_ICON_TICKET_FILL_SALES_STATUS,
        eventStatusNarrowIconColor: ORG_ICON_GREEN_COLOR,
    },
    [EVENT_SALES_STATUS_NOT_YET_ON_SALE]: {
        eventStatusIconColor: ORG_ICON_YELLOW_COLOR,
        eventStatusNarrowIconType: NARROW_ICON_TICKET_FILL_SALES_STATUS,
        eventStatusNarrowIconColor: ORG_ICON_YELLOW_COLOR,
    },
    [EVENT_SALES_STATUS_NOT_YET_ON_SALE_ACCESS_CODED]: {
        eventStatusLabel: lazyGettext('On Sale Soon'),
        eventStatusIconColor: ORG_ICON_YELLOW_COLOR,
        eventStatusNarrowIconType: NARROW_ICON_TICKET_FILL_SALES_STATUS,
        eventStatusNarrowIconColor: ORG_ICON_YELLOW_COLOR,
    },
    [EVENT_SALES_STATUS_MESSAGE_CODE_TICKETS_AT_THE_DOOR]: {
        eventStatusLabel: lazyGettext('At The Door'),
        eventStatusIconColor: ORG_ICON_COLOR,
        eventStatusNarrowIconType: NARROW_ICON_TICKET_FILL_OFF_SALES_STATUS,
        eventStatusNarrowIconColor: ORG_ICON_COLOR,
    },
    [EVENT_SALES_STATUS_DEFAULT_VALUES]: {
        eventStatusIconColor: ORG_ICON_COLOR,
        eventStatusNarrowIconType: NARROW_ICON_TICKET_FILL_OFF_SALES_STATUS,
        eventStatusNarrowIconColor: ORG_ICON_COLOR,
    },
};

export const EVENT_TEXT_CODE_TICKETS_NOT_YET_ON_SALE_KEY =
    'tickets_not_yet_on_sale';
export const EVENT_TEXT_CODE_TICKETS_WITH_SALES_ENDED_KEY =
    'tickets_with_sales_ended';
export const EVENT_TEXT_CODE_TICKETS_SOLD_OUT_KEY = 'tickets_sold_out';
export const EVENT_SALES_STATUS_CUSTOM_MESSAGE = lazyGettext('Custom');

// Janus Checkpoints for publish
export const FIRST_CREATE_EVENT_PUBLISH = 'first_create_event_publish';
export const FIRST_CREATE_EVENT_PAID_PUBLISH =
    'first_create_event_paid_publish';
export const CREATE_EVENT_PUBLISH = 'create_event_publish_frontend';

export const DISPLAY_SALES_STATUS_ON_SALE = 'on_sale';
export const DISPLAY_SALES_STATUS_NOT_YET_ON_SALE = 'not_yet_on_sale';
export const DISPLAY_SALES_STATUS_SALES_ENDED = 'sales_ended';
export const DISPLAY_SALES_STATUS_SOLD_OUT = 'sold_out';
export const DISPLAY_SALES_STATUS_TICKETS_ATD = 'tickets_at_the_door';
export const DISPLAY_SALES_STATUS_CUSTOM = 'custom';
export const DISPLAY_SALES_STATUS_NOT_YET_ON_SALE_ACCESS_CODED = 'on_sale_soon';
export const DISPLAY_SALES_STATUS_CANCELLED = 'event_cancelled';
export const DISPLAY_SALES_STATUS_POSTPONED = 'event_postponed';
export const DISPLAY_SALES_STATUS_UNAVAILABLE = 'unavailable';

export const LOADING_EVENT_SALES_STATUS_FORM = 'event-sales-status-load-start';
export const LOAD_EVENT_SALES_STATUS_COMPLETE =
    'event-sales-status-load-complete';

export const UNSCHEDULE_MODAL_COPY = lazyGettext(
    "By unscheduling your publish date and time, you'll need to manually publish this event or reschedule it.",
);
